import { HowToInstallHero } from "@/components/howToInstallHero/howToInstallHero.comp";
import { InstallGuide } from "@/components/installGuide/installGuide.comp";
import { Layout } from "@/components/layout/layout.comp";
import { Testimonials } from "@/components/testimonials/testimonials.comp";
import { WidgetVideoGuide } from "@/components/widgetVideoGuide/widgetVideoGuide.comp";
import { emptyApp, emptyPlatform } from "@/lib/empty";
import { getDataFromDB } from "@/lib/mongodb";
import { IPage, IPlatform, IPlugin } from "@/types";
import { GetServerSidePropsContext } from "next";
import Script from "next/script";

export default function HowToAdd({ app, platform, videoUrl }: { app: IPlugin, platform: IPlatform, videoUrl: string; }) {
  if (!app.slug) {
    return <></>;
  }

  return (
    <Layout
      showHeader={true}
      showFooter={true}
      isDark
      title={`How to add ${app.name} to ${platform.title}`}
      url={`https://www.bracketsninja.com/platforms/${platform.slug}/how-to-add`}
    >
      <HowToAddScripts app={app} platform={platform} />
      <HowToInstallHero platform={platform} app={app} />
      <WidgetVideoGuide widget={app} videoUrl={videoUrl} />
      <InstallGuide text={`Embed ${app.name} on ${platform.title}`} variant='list' app={app} platform={platform} instructions={platform.instructions} />
      <Testimonials mode="dark" />
    </Layout>
  );
}

const HowToAddScripts = ({ app, platform }: { app: IPlugin, platform: IPlatform; }) => {
  let steps = [
    {
      title: "Copy the [app_name] [app_type]’s code.",
      step: `To add Common Ninja’s [app_name] [app_type] to your [platform] [site_type], you have to do the following: 
        First, go to the “Add to Website” tab on the Common Ninja Editor’s dashboard and copy the HTML code.`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1644237137726_image1.png",
    },
    {
      title: "Paste the code to you [platform] [site_type]’s HTML field.",
      step: `Next, go to your [platform] [site_type] and find the HTML field. It is most likely in the form of an HTML/Code widget or module which you can find on [platform]’s dashboard. Add it to your page, and paste the code you’ve copied before to the HTML field. Save and update, and that’s it, you should have Common Ninja’s [app_name] [app_type] on your [platform] [site_type].`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1644237151468_image2.png",
    },
    {
      title:
        "Alternative Method 1: Through the HTML option in the text’s editing area.",
      step: `Should you have difficulties finding the widget or the module you can use an alternative method of adding Common Ninja’s [app_name] [app_type] to your [platform] [site_type] — adding the code to a special, HTML area in the text editing field.`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1644237160792_image3.png",
    },
    {
      title:
        "Alternative Method 2: Add it directly to your [site_type]’s HTML code.",
      step: `Should you have difficulties finding a widget, or the special area in your text editing field, you can use an alternative method of adding Common Ninja’s [app_name] [app_type] to your [platform] [site_type] — adding it directly to your [site_type]’s HTML code.`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1644237172811_image4.png",
    },
  ];

  if (platform.instructions.length >= 3) {
    steps = platform.instructions;
  }

  function capitalize(s: string): string {
    if (!s) return "";
    return s[0].toUpperCase() + s.slice(1);
  }

  function replaceWords(s: string) {
    return (s || '')
      ?.replace(/\[app_type\]/g, platform.appSynonym)
      ?.replace(/\[App_type\]/g, capitalize(platform.appSynonym))
      ?.replace(/\[platform\]/g, platform.title)
      ?.replace(/\[site_type\]/g, platform.siteSynonym)
      ?.replace(/\[Site_type\]/g, capitalize(platform.siteSynonym))
      ?.replace(/\[app_name\]/g, app.name);
  }

  function renderSchemaSteps() {
    let instructionsStepsStr = ``;

    steps.forEach((step, idx) => {
      const newStep = `{
        "@type": "HowToSection",
        "name": "${(replaceWords(step.title.replace(/"/g, '\\"')).replace(/<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g, '')) || `Step ${idx + 1}`}",
        "itemListElement": [{
          "@type": "HowToStep",
          "position": "${idx + 1}",
          "text": "${(replaceWords(step.step.replace(/"/g, '\\"')).replace(/<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g, ''))}",
          "image": "${step.imgUrl || ''}"
        }]
      }${steps.length === idx + 1 ? '' : ','}`;

      instructionsStepsStr += newStep;
    });

    return instructionsStepsStr;
  };

  return (
    <Script id="instructions-script" type="application/ld+json">{`
   {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "Add ${app.name} to ${platform.title}",
  "totalTime": "PT5M",
  "step": [${renderSchemaSteps()}],
  "tool": [
    {
      "@type": "HowToTool",
      "name":  "${platform.title} editor"
    },
    {
      "@type": "HowToTool",
      "name": "Web browser"
    }
  ],
"supply": [
    {
      "@type": "HowToSupply",
      "name": "${app.name} embed code"
    }
  ]
}
      `}</Script>
  );
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { slug } = context.query;
  const apps = await getDataFromDB<IPlugin>("apps", {
    slug: 'brackets',
    status: 'published'
  });

  const platforms = await getDataFromDB<IPlatform>("platforms", {
    slug,
    status: 'published'
  });

  const [page] = await getDataFromDB<IPage>("pages", {
    appId: apps?.[0]?._id,
    platformId: platforms?.[0]?._id,
    status: 'published'
  }, {
    videoUrl: 1
  });

  if (!page || !apps[0] || !platforms[0]) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }

  return {
    props: {
      app: apps[0] || emptyApp(),
      platform: platforms[0] || emptyPlatform(),
      videoUrl: page.videoUrl || ''
    }
  };
}


